import React from 'react';
import { Calendar } from 'lucide-react';

interface DateInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  showCurrent?: boolean;
  isCurrent?: boolean;
  onCurrentChange?: (value: boolean) => void;
  showPursuing?: boolean;
  isPursuing?: boolean;
  onPursuingChange?: (value: boolean) => void;
}

export function DateInput({
  label,
  value,
  onChange,
  required = false,
  showCurrent = false,
  isCurrent = false,
  onCurrentChange,
  showPursuing = false,
  isPursuing = false,
  onPursuingChange
}: DateInputProps) {
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 30 }, (_, i) => currentYear + 5 - i);
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const [year, month] = value ? value.split('-') : ['', ''];
  const isDisabled = (showCurrent && isCurrent) || (showPursuing && isPursuing);

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        <Calendar className="w-4 h-4 inline mr-1" />
        {label}
      </label>
      
      <div className="flex gap-2">
        <select
          value={month}
          onChange={(e) => {
            const newMonth = e.target.value.padStart(2, '0');
            onChange(year ? `${year}-${newMonth}` : '');
          }}
          required={required && !isDisabled}
          disabled={isDisabled}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm 
            focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
        >
          <option value="">Month</option>
          {months.map((month, index) => (
            <option key={month} value={(index + 1).toString().padStart(2, '0')}>
              {month}
            </option>
          ))}
        </select>

        <select
          value={year}
          onChange={(e) => {
            const newYear = e.target.value;
            onChange(month ? `${newYear}-${month}` : newYear ? `${newYear}-01` : '');
          }}
          required={required && !isDisabled}
          disabled={isDisabled}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm 
            focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
        >
          <option value="">Year</option>
          {years.map(year => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>

      {showCurrent && (
        <div className="flex items-center mt-2">
          <input
            type="checkbox"
            id={`current-${label.replace(/\s+/g, '-').toLowerCase()}`}
            checked={isCurrent}
            onChange={(e) => onCurrentChange?.(e.target.checked)}
            className="rounded border-gray-300 text-indigo-600 
              focus:ring-indigo-500"
          />
          <label 
            htmlFor={`current-${label.replace(/\s+/g, '-').toLowerCase()}`} 
            className="ml-2 text-sm text-gray-600"
          >
            Current Position
          </label>
        </div>
      )}

      {showPursuing && (
        <div className="flex items-center mt-2">
          <input
            type="checkbox"
            id={`pursuing-${label.replace(/\s+/g, '-').toLowerCase()}`}
            checked={isPursuing}
            onChange={(e) => onPursuingChange?.(e.target.checked)}
            className="rounded border-gray-300 text-indigo-600 
              focus:ring-indigo-500"
          />
          <label 
            htmlFor={`pursuing-${label.replace(/\s+/g, '-').toLowerCase()}`} 
            className="ml-2 text-sm text-gray-600"
          >
            Currently Pursuing
          </label>
        </div>
      )}
    </div>
  );
}